import React, { useCallback, useState } from 'react';

import Button from '../../../../library/button/button';
import Collapsible from '../../../../library/collapsible/collapsible';
import Image from '../../../../library/image/image';
import Link from '../../../../library/link/link';
import Typography from '../../../../library/typography/typography';
import VehicleDescription from '../../../../library/vehicle-description/vehicle-description';
import VehicleLicenseVinDetails from '../../../../library/vehicle-license-vin-details/vehicle-license-vin-details';
import FitmentSize from '../../../../components/fitment-size/fitment-size';
import TreadwellPersonaIcon from '../../../../components/treadwell-persona-icon/treadwell-persona-icon';
import VehicleImage from '../../../../components/vehicle-image/vehicle-image';
import CollapsibleToggle from '../../../../library/collapsible/components/collapsible-toggle/collapsible-toggle';
import WinterToggle from '../winter-toggle/winter-toggle';

import {
  useFitment,
  useMyAccount,
  useNavigateTo,
  useTireWheelPackage,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';

import './product-list-driving-profile.scss';

interface ProductListDrivingProfileProps {
  onWinterToggle: () => void;
  showTreadwellProfile: boolean;
}

function ProductListDrivingProfile(props: ProductListDrivingProfileProps) {
  const { onWinterToggle, showTreadwellProfile = false } = props;

  const [isOpen, setIsOpen] = useState(true);

  const { partialFitment, selectedFitment } = useFitment();
  const { treadwellPreferences } = useTreadwellPreferences();
  const { confirmCancelTireWheelPackage, incompletePackage } =
    useTireWheelPackage();
  const { isFleetAccount } = useMyAccount();
  const navigateTo = useNavigateTo();

  const { selectedDriverType, zipCodeWeatherData } = treadwellPreferences || {};
  const { icon, name } = selectedDriverType || {};
  const { city, state } = zipCodeWeatherData || {};

  const fitment = selectedFitment || partialFitment;
  const {
    frontTireSize,
    frontWheelDiameter,
    isOE,
    isStaggered,
    make,
    model,
    rearTireSize,
    rearWheelDiameter,
    rideHeight,
    sizeQualifier,
    smallImageUrl,
    trim,
    year
  } = fitment || {};

  const showLicenseVin =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;

  const handleEditVehicle = useCallback(async () => {
    if (incompletePackage) {
      await confirmCancelTireWheelPackage();
    } else {
      navigateTo('#/fitment/vehicle/results');
    }
  }, [confirmCancelTireWheelPackage, incompletePackage, navigateTo]);

  return (
    <div styleName="container">
      {fitment ? (
        <>
          <header styleName="heading">
            {showTreadwellProfile ? (
              <CollapsibleToggle
                isOpen={isOpen}
                label={
                  <>
                    <Image
                      alt="Treadwell"
                      height="24px"
                      src="https://discounttire.scene7.com/is/content/discounttire/logos/treadwell-icon-only-trademark.svg"
                      styleName="treadwell-icon"
                    />
                    <Typography tag="span" weight="bold">
                      Your Treadwell™ Profile
                    </Typography>
                  </>
                }
                styleName="treadwell-title"
                toggleCollapsible={() => setIsOpen(!isOpen)}
                toggleType={'CHEVRON'}
              />
            ) : (
              <>
                <Typography tag="span" weight="bold">
                  Your Driving Profile
                </Typography>
                <Link
                  onClick={() => trackAction('vehicle_page_edit')}
                  size="small"
                  styleName="edit"
                  to="#/fitment/vehicle/edit/confirm"
                >
                  edit
                </Link>
              </>
            )}
          </header>

          <div styleName="vehicle">
            <VehicleImage
              alt="vehicle"
              smallImageUrl={smallImageUrl}
              styleName="vehicle-image"
            />
            <div styleName="vehicle-details">
              <VehicleDescription
                make={make}
                model={model}
                rideHeight={rideHeight}
                trim={trim}
                year={year}
              />
              {showLicenseVin && (
                <VehicleLicenseVinDetails
                  license={fitment?.licensePlate}
                  vin={fitment?.vin}
                />
              )}
            </div>
          </div>

          {!showTreadwellProfile && (
            <>
              {fitment && frontTireSize && (
                <div styleName="size-container">
                  <div styleName="size">
                    <FitmentSize
                      isOE={isOE}
                      sizeQualifier={sizeQualifier}
                      tireLocation={isStaggered ? 'front' : undefined}
                      tireSize={frontTireSize}
                      wheelDiameter={frontWheelDiameter}
                    />
                  </div>

                  {isStaggered &&
                    (Boolean(rearTireSize) || Boolean(rearWheelDiameter)) && (
                      <div styleName="size">
                        <FitmentSize
                          isOE={isOE}
                          sizeQualifier={sizeQualifier}
                          tireLocation="rear"
                          tireSize={rearTireSize}
                          wheelDiameter={rearWheelDiameter}
                        />
                      </div>
                    )}
                </div>
              )}
            </>
          )}

          {showTreadwellProfile && (
            <>
              <Collapsible hideToggle isOpen={isOpen} styleName="persona">
                <Typography styleName="driver-label" tag="div">
                  Driver Type
                </Typography>
                <Typography styleName="driver-value" tag="div" weight="bold">
                  {name && <TreadwellPersonaIcon iconName={icon} />}{' '}
                  {name || 'Custom'}
                </Typography>
                <Typography styleName="driver-label" tag="div">
                  Driving Location
                </Typography>
                <Typography styleName="driver-value" tag="div" weight="bold">
                  <span styleName="city">{city?.toLowerCase()}</span>, {state}
                </Typography>
                <WinterToggle onToggle={onWinterToggle} />
              </Collapsible>

              <Button isFullWidth isSecondary onClick={handleEditVehicle}>
                edit my info
              </Button>
            </>
          )}
        </>
      ) : (
        <>
          <VehicleImage alt="no vehicle" styleName="no-vehicle-image" />
          <Typography styleName="prompt" weight="black">
            Tell us about your vehicle to ensure these products fit
          </Typography>
          <Button
            href="#/fitment/vehicle"
            isFullWidth
            isSecondary
            styleName="get-started-btn"
          >
            Get Started
          </Button>
        </>
      )}
    </div>
  );
}

export default ProductListDrivingProfile;
